/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import L, { DomUtil, LatLng, LatLngBounds, Map, Point } from 'leaflet';
import { MOUSE_BUTTONS } from '../../annotations.interface';

export const initPathDrag = (): void => {
  L.PathDraggable = L.Draggable.extend({
    initialize: function (path: any) {
      this._path = path;
      this._canvas = path._map.getRenderer(path) instanceof L.Canvas;
      const element = this._canvas ? this._path._map.getRenderer(this._path)._container : this._path._path;
      L.Draggable.prototype.initialize.call(this, element, element, true);
    },

    _updatePosition: function (): void {
      const e = { originalEvent: this._lastEvent };
      this.fire('drag', e);
    },

    _onDown(e: any) {
      if (!(e.buttons & MOUSE_BUTTONS.LEFT)) {
        return;
      }
      const first = e.touches ? e.touches[0] : e;
      this._startPoint = new L.Point(first.clientX, first.clientY);
      if (this._canvas && !this._path._containsPoint(this._path._map.mouseEventToLayerPoint(first))) {
        return;
      }
      L.Draggable.prototype._onDown.call(this, e);
    },
  });

  L.Handler.PathDrag = L.Handler.extend({
    // Adding a new property to the class
    wasOrientationMarkerInitialized: false,
    orientationMarker: undefined,

    initialize: function (path: L.Path) {
      this._path = path;
    },

    getEvents: function (): { dragstart: any; drag: any; dragend: any } {
      return {
        dragstart: this._onDragStart,
        drag: this._onDrag,
        dragend: this._onDragEnd,
      };
    },

    // Redefining a method
    addHooks: function (): void {
      if (!this._draggable) {
        this._draggable = new L.PathDraggable(this._path);
      }
      this._draggable.on(this.getEvents(), this).enable();
      DomUtil.addClass(this._draggable._element, 'leaflet-path-draggable');
    },

    calculateOffset: function (e: any): { start: Point; offset: Point } {
      const event = e.originalEvent.touches && e.originalEvent.touches.length === 1 ? e.originalEvent.touches[0] : e.originalEvent;
      const nextStartPoint = L.point(event.clientX, event.clientY);
      const currentOffset: Point = nextStartPoint.subtract(this._startPoint);

      return { start: nextStartPoint, offset: currentOffset };
    },

    initializeOrientationMarker(map: Map) {
      this.wasOrientationMarkerInitialized = true;
      const storeKey = this._path.options.storeKey;

      if (storeKey && map) {
        map.eachLayer((layer: any) => {
          if (layer.options.storeKey === `${storeKey}-orientation-marker`) {
            this.orientationMarker = layer;
          }
        });
      }
    },

    _onDrag: function (e: any) {
      const map: Map = this._path._map;
      if (!this.wasOrientationMarkerInitialized) {
        this.initializeOrientationMarker(map);
      }

      const offsetCalculationResult: { start: Point; offset: Point } = this.calculateOffset(e);
      this._offset = offsetCalculationResult.offset;
      this._startPoint = offsetCalculationResult.start;
      this._path.eachLatLng(this.updateLatLng, this);

      if (this.orientationMarker) {
        this.orientationMarker.eachLatLng(this.updateLatLng, this);
        this.orientationMarker.redraw();
      }

      this._path.redraw();

      e.latlng = this._path._map.layerPointToLatLng(this._startPoint);
      e.offset = this._offset;
      this._path.fire('drag', e);

      e.latlng = this._path.getCenter ? this._path.getCenter() : this._path.getLatLng();

      this._path.fire('move', e);
    },

    removeHooks: function (): void {
      this._draggable.off(this.getEvents(), this).disable();
      L.DomUtil.removeClass(this._draggable._element, 'leaflet-path-draggable');
    },

    moved: function (): boolean {
      return !!this._draggable && !!this._draggable._moved;
    },

    _onDragStart: function (): void {
      this._startPoint = this._draggable._startPoint;
      this._path.closePopup().fire('movestart').fire('dragstart');
    },

    _onDragEnd: function (e: any): void {
      if (this._path._bounds) {
        this.resetBounds();
      }
      this._path.fire('moveend').fire('dragend', e);
    },

    latLngToLayerPoint: function (latlng: LatLng): Point {
      // Same as map.latLngToLayerPoint, but without the round().
      const path = this._path as Path;
      const projectedPoint: Point = path._map.project(L.latLng(latlng));
      const point = projectedPoint._subtract(path._map.getPixelOrigin());
      return point;
    },

    updateLatLng: function (latlng: LatLng) {
      const oldPoint = this.latLngToLayerPoint(latlng);
      oldPoint._add(this._offset);
      const newLatLng = this._path._map.layerPointToLatLng(oldPoint);
      latlng.lat = newLatLng.lat;
      latlng.lng = newLatLng.lng;
    },

    resetBounds: function (): void {
      this._path._bounds = new LatLngBounds([]);
      this._path.eachLatLng((latlng: any) => {
        this._path._bounds.extend(latlng);
      });
    },
  });

  L.Path.include({
    eachLatLng: function (callback: any, context: any) {
      context = context || this;
      const loop = function (latlngs: any): void {
        for (let i = 0; i < latlngs.length; i++) {
          if (L.Util.isArray(latlngs[i])) {
            loop(latlngs[i]);
          } else {
            callback.call(context, latlngs[i]);
          }
        }
      };
      loop(this.getLatLngs ? this.getLatLngs() : [this.getLatLng()]);
    },
  });

  L.Path.addInitHook(function (): void {
    this.dragging = new L.Handler.PathDrag(this);
    if (this.options.draggable) {
      this.once('add', () => {
        this.dragging.enable();
      });
    }
  });
};
